import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { providersService } from '../infrastructure/providers.service';
import type {
  SetProviderScheduleInput,
  CreateScheduleOverrideInput,
} from '../infrastructure/providers.service';

export const providerKeys = {
  all: ['providers'] as const,
  lists: () => [...providerKeys.all, 'list'] as const,
  list: () => [...providerKeys.lists()] as const,
  details: () => [...providerKeys.all, 'detail'] as const,
  detail: (id: string) => [...providerKeys.details(), id] as const,
  schedules: () => [...providerKeys.all, 'schedule'] as const,
  schedule: (id: string, from?: string, to?: string) =>
    [...providerKeys.schedules(), id, { from, to }] as const,
  availabilitySummary: (date: string) => [...providerKeys.all, 'availability-summary', date] as const,
};

export function useProviderList() {
  return useQuery({
    queryKey: providerKeys.list(),
    queryFn: () => providersService.list(),
  });
}

export function useProvider(id: string | undefined) {
  return useQuery({
    queryKey: providerKeys.detail(id!),
    queryFn: () => providersService.getById(id!),
    enabled: !!id,
  });
}

export function useProviderSchedule(id: string, from?: string, to?: string) {
  return useQuery({
    queryKey: providerKeys.schedule(id, from, to),
    queryFn: () => providersService.getSchedule(id, from, to),
    enabled: !!id,
  });
}

export function useSetProviderSchedule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, schedules }: { id: string; schedules: SetProviderScheduleInput[] }) =>
      providersService.setSchedule(id, schedules),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.schedules() });
    },
  });
}

export function useAddScheduleOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateScheduleOverrideInput }) =>
      providersService.addOverride(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.schedules() });
    },
  });
}

export function useRemoveScheduleOverride() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, overrideId }: { id: string; overrideId: string }) =>
      providersService.removeOverride(id, overrideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: providerKeys.schedules() });
    },
  });
}

export function useProviderAvailabilitySummary(date: string) {
  return useQuery({
    queryKey: providerKeys.availabilitySummary(date),
    queryFn: () => providersService.getAvailabilitySummary(date),
    enabled: !!date,
  });
}
